import { Camera } from "lucide-react";
import { cn } from "@/shared/lib/utils";
import { useEvidence, type EvidenceCaption } from "./EvidenceLightbox";

interface EvidenceBadgeProps {
  urls: string[];
  caption?: EvidenceCaption;
  size?: "sm" | "md";
}

/**
 * Camera-icon button that opens the evidence lightbox on click. Visible on
 * any row / item / KPI tile whose source leaves have at least one Firebase
 * Storage URL projected through agent_survey_leaves.evidence (P4 migration).
 *
 * Min hit target is 24×24 even at "sm" — meets the accessibility floor we
 * deferred per R6 but still want for muscle memory.
 */
export function EvidenceBadge({ urls, caption, size = "md" }: EvidenceBadgeProps) {
  const evidence = useEvidence();
  if (!urls || urls.length === 0) return null;
  const count = urls.length;

  return (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        evidence.open({ urls, caption });
      }}
      title={`${count} evidence photo${count === 1 ? "" : "s"}`}
      className={cn(
        "inline-flex items-center justify-center gap-1 rounded-md border border-border bg-card/80 text-muted-foreground transition-colors",
        "hover:bg-accent hover:text-foreground hover:border-primary/40",
        size === "sm" ? "h-6 w-6 px-0" : "h-7 w-auto min-w-[44px] px-2",
      )}
      aria-label={`Open ${count} evidence photo${count === 1 ? "" : "s"}`}
    >
      <Camera className={size === "sm" ? "h-3 w-3" : "h-3.5 w-3.5"} />
      {count > 1 && size !== "sm" ? (
        <span className="text-[10px] font-semibold tabular-nums">{count}</span>
      ) : null}
    </button>
  );
}
